import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Star, CheckCircle } from "lucide-react";
import { getSellerLogoUrl } from "@/lib/utils/avatars";
import { MarketplaceCard } from "@/components/ui/marketplace-card";

interface SellerCardProps {
  id: string;
  name: string;
  bio: string;
  logoUrl: string;
  verified: boolean;
  offerCount: number;
  rating: number;
  reviewCount: number;
  tags: string[];
  slug?: string;
}

const SellerCard = ({
  id,
  name,
  bio,
  logoUrl,
  verified,
  offerCount,
  rating,
  reviewCount,
  tags,
  slug,
}: SellerCardProps) => {
  // Use slug if available, otherwise fallback to ID
  const profileUrl = slug ? `/sellers/${slug}` : `/sellers/${id}`;
  
  // Get the logo URL, falling back to UI Avatars if none is provided
  const sellerLogoUrl = getSellerLogoUrl(logoUrl, name);
  
  // Title component with seller name and verification icon
  const titleComponent = (
    <div className="flex items-center">
      <Link to={profileUrl} className="h-10 w-10 sm:h-12 sm:w-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200 mr-3">
        <img 
          src={sellerLogoUrl} 
          alt={name} 
          className="h-full w-full object-cover"
        />
      </Link>
      <div>
        <div className="flex items-center">
          <span className="font-semibold text-sm sm:text-base truncate max-w-[150px]">{name}</span>
          {verified && (
            <CheckCircle className="h-4 w-4 text-green-500 ml-1 flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center text-xs">
          <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400 mr-1 flex-shrink-0" />
          <span>{rating} ({reviewCount} reviews)</span>
        </div>
      </div>
    </div>
  );
  
  // Link offers count
  const subtitleComponent = (
    <div className="text-xs text-gray-500 pl-1">{offerCount} link offers</div>
  );
  
  // Tags list
  const tagsSection = (
    <div className="flex flex-wrap gap-1 mt-3">
      {tags.map((tag, index) => (
        <Badge key={index} variant="secondary" className="bg-gray-100 hover:bg-gray-200 text-xs px-2 py-0">
          {tag}
        </Badge>
      ))}
    </div>
  );
  
  return (
    <MarketplaceCard
      title={titleComponent}
      subtitle={subtitleComponent}
      description={bio}
      linkTo={profileUrl}
      actionLabel="View Profile"
      className="h-full"
      contentClassName="pt-4"
    >
      {tagsSection}
    </MarketplaceCard>
  );
};

export default SellerCard;
